import type { EventEmitter } from './event-emitter'

export type DesignTokens = Record<string, string | null>

const BRAND_CONFIG_UPDATE_EVENT = 'brandConfigUpdate'
const DESIGN_TOKENS_CHANGED_EVENT = 'designTokensChanged'

let tokens: DesignTokens | null = null
let initialized = false
const subscribers = new Set<EventEmitter>()

function extractTokensFromStylesheet(sheet: CSSStyleSheet | null): DesignTokens {
  if (!sheet) return {}

  const result: DesignTokens = {}
  try {
    const rules = sheet.cssRules
    for (let i = 0; i < rules.length; i++) {
      const rule = rules[i]
      if (rule instanceof CSSStyleRule && rule.style) {
        for (let j = 0; j < rule.style.length; j++) {
          const prop = rule.style[j]
          if (prop.startsWith('--config')) {
            result[prop.slice(2)] = rule.style.getPropertyValue(prop).trim()
          }
        }
      }
    }
  } catch {
    // CORS-restricted stylesheets throw — return empty
  }
  return result
}

/**
 * Styles that may declare design tokens, in cascade order — later entries win.
 *
 * - `css-variables` — the saved theme, server-rendered on community pages.
 * - `gs-cc-design-tokens` — published by the SDK on embed host pages, where the
 *   community's own `#css-variables` is not present.
 * - `css-variables-preview` — unsaved edits from the theme configurator, which override
 *   the saved theme until published.
 */
const TOKEN_STYLE_IDS = ['css-variables', 'gs-cc-design-tokens', 'css-variables-preview']

function readTokensFromDOM(): DesignTokens {
  const result: DesignTokens = {}

  for (const id of TOKEN_STYLE_IDS) {
    const el = document.getElementById(id)
    const sheet = el instanceof HTMLStyleElement || el instanceof HTMLLinkElement ? el.sheet : null
    Object.assign(result, extractTokensFromStylesheet(sheet))
  }

  return result
}

function handleBrandConfigUpdate(event: Event): void {
  if (!event || !('detail' in event)) return
  const data = (event as CustomEvent).detail?.data
  if (!data || typeof data !== 'object') return

  const normalized: DesignTokens = {}
  for (const key of Object.keys(data)) {
    const cleanKey = key.startsWith('--') ? key.slice(2) : key
    normalized[cleanKey] = data[key]
  }

  tokens = { ...tokens, ...normalized }

  const snapshot = { ...tokens }
  for (const emitter of subscribers) {
    emitter.emit(DESIGN_TOKENS_CHANGED_EVENT, snapshot)
  }
}

function ensureInitialized(): void {
  if (initialized) return
  initialized = true
  tokens = readTokensFromDOM()
  window.addEventListener(BRAND_CONFIG_UPDATE_EVENT, handleBrandConfigUpdate)
}

export function getDesignTokens(): DesignTokens {
  ensureInitialized()
  return { ...(tokens ?? {}) }
}

export function registerEmitter(emitter: EventEmitter): void {
  ensureInitialized()
  subscribers.add(emitter)
}

export function unregisterEmitter(emitter: EventEmitter): void {
  subscribers.delete(emitter)
}

/** @internal */
export function _resetForTesting(): void {
  tokens = null
  initialized = false
  subscribers.clear()
  window.removeEventListener(BRAND_CONFIG_UPDATE_EVENT, handleBrandConfigUpdate)
}
